import {NetworkNode, RouterNetworkTree} from "../../../../../../services/back/infrastructure-schema.service";

interface IntercomChainInfo {
	routerName: string;
	depth: number;
	details: RouterIntercomDetails;
}

interface RouterIntercomDetails {
	routerName: string;
	intercomNetworks: NetworkIntercomDetail[];
}

interface NetworkIntercomDetail {
	networkName: string;
	role: string;
	connectedRoutersCount: number;
	connectedRouters: {
		name: string;
		chainDepth: number;
	}[];
}

export function processInfrastructure(infrastructure: RouterNetworkTree[]): RouterNetworkTree[] {
	// Étape 1: Supprimer les réseaux dupliqués
	const deduplicatedInfrastructure = removeDuplicateNetworks(infrastructure);

	// Étape 2: Organiser les routeurs en arbre équilibré
	return organizeRoutersBalanced(deduplicatedInfrastructure);
}

/**
 * Fonction principale pour organiser les routeurs dans un arbre équilibré
 * Le plus grand au centre, puis alternance droite/gauche par ordre de grandeur
 * @param infrastructure - L'infrastructure réseau complète
 * @returns L'infrastructure réorganisée pour un affichage équilibré
 */
export function organizeRoutersBalanced(infrastructure: RouterNetworkTree[]): RouterNetworkTree[] {
	// Étape 1: Faire une copie profonde pour ne pas modifier l'original
	const routersCopy: RouterNetworkTree[] = JSON.parse(JSON.stringify(infrastructure));

	// Étape 2: Calculer la profondeur de chaque routeur et créer un tableau avec cette information
	const routersWithDepth = routersCopy.map(router => ({
		router: router,
		chainDepth: calculateIntercomChainDepth(router)
	}));

	// Étape 3: Trier ce tableau par profondeur décroissante
	routersWithDepth.sort((a, b) => b.chainDepth - a.chainDepth);

	// Étape 4: Réorganiser les routeurs dans l'ordre centre-droite-gauche-droitedu2-gauchedu3, etc.
	const reorganizedRouters: RouterNetworkTree[] = [];

	// Placer d'abord le routeur avec la chaîne la plus longue (le plus important)
	if (routersWithDepth.length > 0) {
		reorganizedRouters.push(routersWithDepth[0].router);

		// Organiser les routeurs restants en alternant droite et gauche
		let rightSide: RouterNetworkTree[] = [];
		let leftSide: RouterNetworkTree[] = [];

		for (let i = 1; i < routersWithDepth.length; i++) {
			// Les positions impaires vont à droite (2ème, 4ème, etc.)
			if (i % 2 === 1) {
				rightSide.push(routersWithDepth[i].router);
			}
			// Les positions paires vont à gauche (3ème, 5ème, etc.)
			else {
				leftSide.push(routersWithDepth[i].router);
			}
		}

		// Ajouter d'abord tous les routeurs de droite, puis tous ceux de gauche
		reorganizedRouters.push(...rightSide);
		reorganizedRouters.push(...leftSide);
	}

	// Étape 5: Appliquer le même principe de réorganisation aux sous-niveaux
	reorganizedRouters.forEach(router => {
		reorganizeSublevels(router);
	});

	return reorganizedRouters;
}

/**
 * Fonction récursive pour réorganiser les sous-niveaux des routeurs
 * @param routerNode - Le nœud routeur dont les sous-niveaux doivent être réorganisés
 */
function reorganizeSublevels(routerNode: RouterNetworkTree): void {
	if (!routerNode.connectedNetworks || routerNode.connectedNetworks.length === 0) {
		return;
	}

	//NOUVEAU EMPÊCHER LE BUG DE NON RETIRAGE DES NETWORKS D'INTERCOMMUNICATION'
	routerNode.connectedNetworks = routerNode.connectedNetworks.filter(network => {
		const networkRole = network.network.network.role;
		// Pour les réseaux d'intercommunication, ne les garder que s'ils ont des routeurs connectés
		if (networkRole === "intercommunication") {
			return network.connectedRouters && network.connectedRouters.length > 0;
		}
		return true;
	});

	// Pour chaque réseau connecté au routeur
	routerNode.connectedNetworks.forEach(networkNode => {
		if (networkNode.connectedRouters && networkNode.connectedRouters.length > 0) {
			// Calculer la profondeur de chaque routeur connecté
			const routersWithDepth = networkNode.connectedRouters.map(router => ({
				router: router,
				chainDepth: calculateIntercomChainDepth(router)
			}));

			// Trier ce tableau par profondeur décroissante
			routersWithDepth.sort((a, b) => b.chainDepth - a.chainDepth);

			// Réorganiser les routeurs
			const reorganizedRouters: RouterNetworkTree[] = [];

			// Placer d'abord le routeur avec la chaîne la plus longue
			if (routersWithDepth.length > 0) {
				reorganizedRouters.push(routersWithDepth[0].router);

				// Organiser les routeurs restants en alternant droite et gauche
				let rightSide: RouterNetworkTree[] = [];
				let leftSide: RouterNetworkTree[] = [];

				for (let i = 1; i < routersWithDepth.length; i++) {
					// Les positions impaires vont à droite
					if (i % 2 === 1) {
						rightSide.push(routersWithDepth[i].router);
					}
					// Les positions paires vont à gauche
					else {
						leftSide.push(routersWithDepth[i].router);
					}
				}

				// Ajouter les routeurs dans l'ordre: centre, droite, gauche
				reorganizedRouters.push(...rightSide);
				reorganizedRouters.push(...leftSide);
			}

			// Remplacer les routeurs connectés par les routeurs réorganisés
			networkNode.connectedRouters = reorganizedRouters;

			// Appliquer récursivement aux niveaux inférieurs
			networkNode.connectedRouters.forEach(childRouter => {
				reorganizeSublevels(childRouter);
			});
		}
	});
}

/**
 * Fonction pour calculer la profondeur maximale des chaînes d'intercommunication
 * @param routerNode - Le nœud routeur à analyser
 * @param visited - Ensemble des IDs de routeurs déjà visités (pour éviter les boucles)
 * @returns La profondeur maximale de la chaîne d'intercommunication
 */
export function calculateIntercomChainDepth(
	routerNode: RouterNetworkTree,
	visited: Set<string> = new Set<string>()
): number {
	// Pour éviter les boucles infinies dans les graphes cycliques
	const routerId = routerNode.router.id;
	if (visited.has(routerId)) {
		return 0;
	}
	visited.add(routerId);

	if (!routerNode.connectedNetworks || routerNode.connectedNetworks.length === 0) {
		return 0;
	}

	let maxChainDepth = 0;

	// Pour chaque réseau connecté au routeur
	routerNode.connectedNetworks.forEach(networkNode => {
		// Vérifie si le réseau a des routeurs connectés (condition pour être un réseau d'intercommunication)
		if (networkNode.connectedRouters && networkNode.connectedRouters.length > 0) {
			let currentNetworkDepth = 1; // Compte ce réseau comme 1 niveau

			// Trouve la chaîne la plus profonde parmi les routeurs connectés
			let maxChildDepth = 0;
			networkNode.connectedRouters.forEach(childRouter => {
				// Clone le Set pour chaque branche d'exploration
				const newVisited = new Set<string>(visited);
				const childDepth = calculateIntercomChainDepth(childRouter, newVisited);
				maxChildDepth = Math.max(maxChildDepth, childDepth);
			});

			// Ajoute la profondeur maximale des enfants
			currentNetworkDepth += maxChildDepth;
			maxChainDepth = Math.max(maxChainDepth, currentNetworkDepth);
		}
	});

	return maxChainDepth;
}

// ----------------------------------------------------------------------------------

/**
 * Fonction pour dédupliquer les réseaux et les routeurs dans l'arborescence
 * Conserve uniquement les occurrences les plus hautes dans l'arbre
 *
 * @param data Arborescence des routeurs et réseaux
 * @returns Arborescence nettoyée
 */
export function removeDuplicates(data: RouterNetworkTree[]): RouterNetworkTree[] {
	// Créer une copie profonde des données
	const clone = JSON.parse(JSON.stringify(data)) as RouterNetworkTree[];

	// ÉTAPE 1: Dédupliquer les réseaux non-intercommunication

	// Collecter tous les chemins vers les réseaux
	const networkPaths: Array<{
		networkId: string;
		networkRole: string;
		routerPath: string[];
		depth: number;
	}> = [];

	// Parcourir l'arborescence pour collecter tous les chemins
	function collectNetworkPaths(
		router: RouterNetworkTree,
		path: string[] = [],
		depth: number = 0
	) {
		const currentPath = [...path, router.router.id];

		// Pour chaque réseau connecté à ce routeur
		for (const network of router.connectedNetworks) {
			const networkId = network.network.network.id;
			const networkRole = network.network.network.role;

			// Ajouter ce chemin à la liste
			networkPaths.push({
				networkId,
				networkRole,
				routerPath: currentPath,
				depth
			});

			// Parcourir récursivement les routeurs connectés
			for (const childRouter of network.connectedRouters) {
				collectNetworkPaths(childRouter, currentPath, depth + 1);
			}
		}
	}

	// Collecter tous les chemins
	for (const router of clone) {
		collectNetworkPaths(router, [], router.depth);
	}

	// Pour chaque réseau non-intercommunication, identifier le meilleur chemin à conserver
	const networksToKeep = new Map<string, {
		routerPath: string[];
		depth: number;
	}>();

	for (const pathInfo of networkPaths) {
		// Ignorer les réseaux d'intercommunication
		if (pathInfo.networkRole === "intercommunication") continue;

		const networkId = pathInfo.networkId;

		// Si c'est le premier chemin ou un meilleur chemin
		if (!networksToKeep.has(networkId) ||
			pathInfo.routerPath.length < networksToKeep.get(networkId)!.routerPath.length ||
			(pathInfo.routerPath.length === networksToKeep.get(networkId)!.routerPath.length &&
				pathInfo.depth < networksToKeep.get(networkId)!.depth)) {

			networksToKeep.set(networkId, {
				routerPath: pathInfo.routerPath,
				depth: pathInfo.depth
			});
		}
	}

	// ÉTAPE 2: Dédupliquer les routeurs

	// Collecter tous les chemins vers les routeurs
	const routerPaths: Array<{
		routerId: string;
		path: string[];
		depth: number;
	}> = [];

	// Parcourir l'arborescence pour collecter tous les chemins vers les routeurs
	function collectRouterPaths(
		router: RouterNetworkTree,
		path: string[] = [],
		depth: number = 0
	) {
		const currentPath = [...path, router.router.id];

		// Ajouter ce routeur à la liste
		routerPaths.push({
			routerId: router.router.id,
			path: path, // Chemin jusqu'à ce routeur (sans inclure le routeur lui-même)
			depth
		});

		// Pour chaque réseau connecté à ce routeur
		for (const network of router.connectedNetworks) {
			// Parcourir récursivement les routeurs connectés
			for (const childRouter of network.connectedRouters) {
				collectRouterPaths(childRouter, currentPath, depth + 1);
			}
		}
	}

	// Collecter tous les chemins vers les routeurs
	for (const router of clone) {
		collectRouterPaths(router, [], router.depth);
	}

	// Pour chaque routeur, identifier le meilleur chemin à conserver
	const routersToKeep = new Map<string, {
		path: string[];
		depth: number;
	}>();

	for (const pathInfo of routerPaths) {
		const routerId = pathInfo.routerId;

		// Si c'est le premier chemin ou un meilleur chemin
		if (!routersToKeep.has(routerId) ||
			pathInfo.path.length < routersToKeep.get(routerId)!.path.length ||
			(pathInfo.path.length === routersToKeep.get(routerId)!.path.length &&
				pathInfo.depth < routersToKeep.get(routerId)!.depth)) {

			routersToKeep.set(routerId, {
				path: pathInfo.path,
				depth: pathInfo.depth
			});
		}
	}

	// ÉTAPE 3: Appliquer la déduplication

	/**
	 * Fonction pour filtrer les réseaux et routeurs
	 * @param router - Le nœud routeur à filtrer
	 * @param path - Le chemin actuel jusqu'à ce routeur
	 * @param depth - La profondeur actuelle dans l'arborescence
	 * @returns Le routeur filtré
	 */
	function filterDuplicates(
		router: RouterNetworkTree,
		path: string[] = [],
		depth: number = 0
	): RouterNetworkTree {
		const currentPath = [...path, router.router.id];

		// Filtrer les réseaux (ne garder que les réseaux d'intercommunication avec des routeurs connectés et les meilleurs chemins)
		router.connectedNetworks = router.connectedNetworks
			.filter(network => {
				const networkId = network.network.network.id;
				const networkRole = network.network.network.role;

				// Pour les réseaux d'intercommunication, ne les garder que s'ils ont des routeurs connectés
				if (networkRole === "intercommunication") {
					return network.connectedRouters && network.connectedRouters.length > 0;
				}

				// Vérifier si ce chemin est le meilleur pour ce réseau
				const bestPath = networksToKeep.get(networkId);
				if (bestPath && arraysEqual(bestPath.routerPath, currentPath)) {
					return true;
				}

				return false;
			})
			.map(network => {
				// Filtrer les routeurs connectés à ce réseau
				network.connectedRouters = network.connectedRouters
					.filter(childRouter => {
						const routerId = childRouter.router.id;

						// Vérifier si ce chemin est le meilleur pour ce routeur
						const bestPath = routersToKeep.get(routerId);
						if (bestPath && arraysEqual(bestPath.path, currentPath)) {
							return true;
						}

						return false;
					})
					.map(childRouter => filterDuplicates(childRouter, currentPath, depth + 1));

				return network;
			});

		return router;
	}

	// Fonction utilitaire pour comparer deux tableaux
	function arraysEqual(a: string[], b: string[]): boolean {
		if (a.length !== b.length) return false;
		for (let i = 0; i < a.length; i++) {
			if (a[i] !== b[i]) return false;
		}
		return true;
	}

	// Appliquer le filtrage sur chaque routeur racine
	return clone.map(router => filterDuplicates(router, [], router.depth));
}

/**
 * Fonction principale - conservée pour compatibilité avec l'ancienne interface
 */
export function removeDuplicateNetworks(data: RouterNetworkTree[]): RouterNetworkTree[] {
	return removeDuplicates(data);
}
